import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { GroupKeyComparatorHelpers, GroupValueHelpers, RecordComparatorHelpers, ResultGroupingDefinition } from '@src/suite/common/data-models/search-results/result-group';
import { ViewStateTypesEnum } from '@src/suite/common/enums/viewstate-types';
import { GlobalTablesGroupSort } from '@src/suite/common/store/global-tables/global-tables.models';

import { i18nKeys } from './i18n.default';

export const TIME_FIELD_SUFFIX = '-Time';

export const calculatePropComparators = (fieldDefs: { [id: string]: FieldDef }, i18n: { [key: string]: string }) => {
    const groupsSort: GlobalTablesGroupSort = {
        groupSortDefs: {
            SingleGroup: new ResultGroupingDefinition(GroupValueHelpers.SingleGroup(), GroupKeyComparatorHelpers.SingleGroup(), null),
            [PSTFieldNames.PSTAssignmentRole]: new ResultGroupingDefinition(
                GroupValueHelpers.RoleOrDefault('Assignment', 'No role'),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom('No role'),
                RecordComparatorHelpers.Generic(PSTFieldNames.PSTAssignmentRole)
            ),
            [PSTFieldNames.PSTModTime]: new ResultGroupingDefinition(
                GroupValueHelpers.DateTime(PSTFieldNames.PSTModTime),
                GroupKeyComparatorHelpers.Value(),
                RecordComparatorHelpers.DateTime(PSTFieldNames.PSTModTime)
            ),
            [PSTFieldNames.Title]: new ResultGroupingDefinition(
                GroupValueHelpers.StringOrDefault(PSTFieldNames.Title, 'No value'),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom('No value'),
                RecordComparatorHelpers.Generic(PSTFieldNames.Title)
            ),
            [PSTFieldNames.Description]: new ResultGroupingDefinition(
                GroupValueHelpers.StringOrDefault(PSTFieldNames.Description, 'No value'),
                GroupKeyComparatorHelpers.DisplayValueDefaultBottom('No value'),
                RecordComparatorHelpers.Generic(PSTFieldNames.Description)
            ),
        },
        [ViewStateTypesEnum.TOPIC]: {
            groupingValues: [],
            sortingValues: [
                { id: PSTFieldNames.Title, title: i18n[i18nKeys.PSTWebSlugLabel] },
                { id: PSTFieldNames.PSTModTime, title: i18n[i18nKeys.PSTWebSortByModTimeLabel] },
                { id: PSTFieldNames.Description, title: fieldDefs[PSTFieldNames.Description].DefaultCaption },
            ],
        },
        [ViewStateTypesEnum.ASSIGNMENT]: {
            groupingValues: [{ id: PSTFieldNames.PSTAssignmentRole, title: i18n[i18nKeys.PSTWebGroupByRoleLabel] }],
            sortingValues: [{ id: PSTFieldNames.Title, title: i18n[i18nKeys.PSTWebSlugLabel] }],
        },
        [ViewStateTypesEnum.SCRIPT]: {
            groupingValues: [],
            sortingValues: [],
        },
        [ViewStateTypesEnum.ROLIST]: {
            groupingValues: [],
            sortingValues: [],
        },
        [ViewStateTypesEnum.SEARCH]: {
            groupingValues: [],
            sortingValues: [],
        },
        [ViewStateTypesEnum.WIRESEARCH]: {
            groupingValues: [],
            sortingValues: [],
        },
    };
    for (const fieldId in fieldDefs) {
        let shouldAdd: boolean = true;
        let isTime: boolean = false;
        const field = fieldDefs[fieldId];
        if (field.TopicProp || field.AssignmentProp || field.StoryProp || field.RundownProp || field.DiscoverWireProp) {
            switch (field.Validation.toLowerCase()) {
                case 'staffidlist': {
                    groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                        GroupValueHelpers.StaffOrDefault(field.ID, 'No value'),
                        GroupKeyComparatorHelpers.DisplayValueDefaultBottom('No value'),
                        RecordComparatorHelpers.StaffOrDefault(field.ID, 'No value')
                    );
                    break;
                }
                case 'select':
                case 'text':
                case 'combobox':
                case 'tree': {
                    groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                        GroupValueHelpers.StringOrDefault(field.ID, 'No value'),
                        GroupKeyComparatorHelpers.DisplayValueDefaultBottom('No value'),
                        RecordComparatorHelpers.Generic(field.ID)
                    );
                    break;
                }
                case 'checklistedit':
                case 'checklist': {
                    groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                        GroupValueHelpers.MultiValueOrDefault(field.ID, 'No value'),
                        GroupKeyComparatorHelpers.DisplayValueDefaultBottom('No value'),
                        RecordComparatorHelpers.MultiValue(field.ID)
                    );
                    break;
                }

                case 'datetimerange': {
                    groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                        GroupValueHelpers.MultiDay(field.ID),
                        GroupKeyComparatorHelpers.Value(),
                        RecordComparatorHelpers.MultiValue(field.ID)
                    );
                    break;
                }

                case 'time': {
                    groupsSort.groupSortDefs[`${field.ID}${TIME_FIELD_SUFFIX}`] = new ResultGroupingDefinition(
                        GroupValueHelpers.Time(field.ID),
                        GroupKeyComparatorHelpers.Value(),
                        RecordComparatorHelpers.DateTime(field.ID)
                    );
                    isTime = true;
                    // falls through
                }
                case 'date': {
                    groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                        GroupValueHelpers.DateTime(field.ID),
                        GroupKeyComparatorHelpers.Value(),
                        RecordComparatorHelpers.DateTime(field.ID)
                    );
                    break;
                }
                case 'viewstateorder': {
                    if (field.ID === PSTFieldNames.PSTTopicOrder) {
                        groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                            GroupValueHelpers.StringOrDefault(PSTFieldNames.PSTTopicOrder, 'No value'),
                            GroupKeyComparatorHelpers.Value(),
                            RecordComparatorHelpers.Manual()
                        );
                    }
                    break;
                }
                case 'checkbox': {
                    groupsSort.groupSortDefs[field.ID] = new ResultGroupingDefinition(
                        GroupValueHelpers.Boolean(field.ID),
                        GroupKeyComparatorHelpers.Value(),
                        RecordComparatorHelpers.Boolean(field.ID)
                    );
                    break;
                }
                default:
                    shouldAdd = false;
            }

            if (field.GroupableProp && shouldAdd) {
                if (field.TopicProp) {
                    groupsSort[ViewStateTypesEnum.TOPIC].groupingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.TOPIC].groupingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
                if (field.AssignmentProp) {
                    groupsSort[ViewStateTypesEnum.ASSIGNMENT].groupingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.ASSIGNMENT].groupingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
                if (field.StoryProp) {
                    groupsSort[ViewStateTypesEnum.SCRIPT].groupingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.SCRIPT].groupingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
                if (field.DiscoverWireProp) {
                    groupsSort[ViewStateTypesEnum.WIRESEARCH].groupingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.WIRESEARCH].groupingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
            }
            if (field.SortableProp && shouldAdd) {
                if (field.TopicProp) {
                    groupsSort[ViewStateTypesEnum.TOPIC].sortingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.TOPIC].sortingValues.push({ id: `${field.ID}${TIME_FIELD_SUFFIX}`, title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}` });
                    }
                }
                if (field.AssignmentProp) {
                    groupsSort[ViewStateTypesEnum.ASSIGNMENT].sortingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.ASSIGNMENT].sortingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
                if (field.StoryProp) {
                    groupsSort[ViewStateTypesEnum.SCRIPT].sortingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.SCRIPT].sortingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
                if (field.DiscoverWireProp) {
                    groupsSort[ViewStateTypesEnum.WIRESEARCH].sortingValues.push({ id: field.ID, title: field.DefaultCaption });
                    if (isTime) {
                        groupsSort[ViewStateTypesEnum.WIRESEARCH].sortingValues.push({
                            id: `${field.ID}${TIME_FIELD_SUFFIX}`,
                            title: `${field.DefaultCaption}-${i18n?.[i18nKeys.PSTWebGroupByTimeLabel]}`,
                        });
                    }
                }
            }
        }
    }
    return groupsSort;
};
